import Vue from 'vue'
import Vuex from 'vuex';
import createdPersistedState from 'vuex-persistedstate';

import account from "./modules/account/index";
import shipping from "./modules/shipping/index";
import billing from "./modules/billing/index";
import checkout from "./modules/checkout/index";
import webstoreForm from "./modules/webstoreForm/index";
import shoppingCart from "./modules/shopping-cart/index";
import content from './modules/content/index';
import cableSelection from './modules/cable-selection/index';
import creditCard from './modules/payment-info/credit-card';
import ach from './modules/payment-info/ach';
import sepa from './modules/payment-info/sepa';
import bacs from './modules/payment-info/bacs';
import acss from './modules/payment-info/acss';

import { cableSelectionRequiredOfferings, cableSelectionAvailableOptions } from '../settings';
import { buildCableSelectionModules } from './utils';

Vue.config.devtools = process.env.NODE_ENV !== "production";

Vue.use(Vuex);

const store = new Vuex.Store({
								state: {
										productCatalog: {},
										currencySymbol: "",
										activeProductFamily: "",
										activeProductKey: "",
										activeProductInfo: {},
										activeOfferKey: "",
										activeOfferQuantity: 1,
										shoppingCartIsOpen: false,
										selectedCategory: "All",
										expiringDiscounts: {},
										total: null,
										currentDate: null,
										activeProductTab: 'overview',
										bagStep: 0,
										isAccountCreatedAfterTitan: false,
										talkToSalesTimeStamp: null,
										talkToSalesButtonClicked: false,
										pricingModalIsOpen: false,
										pricingModalComplete: false,
										anonymousLargeFleet: false,
										isAnonymousCustomer: false,
										isReferralIdCustomer: false,
										sfdcSegments: {
											segmentC: "",
											ownerRole: "",
										},
										isPhoenixPhaseTwoCustomer: false,
										isPhoenixPhaseThreeCustomer: false,
										isPartnerAccount: false,
										renewalDiscountMatrix: {},
										isPhoenixCustomer: false,
										typeOfRenewal: null,
										version: null,
										isVGBasicCustomer: false,
										productSource: null
								},
								getters: {
									productCatalog: state => state.productCatalog,
									currencySymbol: state => state.currencySymbol,
									activeProductFamily: state => state.activeProductFamily,
									activeProductKey: state => state.activeProductKey,
									activeProductInfo: state => {return (state.productCatalog && state.activeProductFamily && state.activeProductKey) ?  state.productCatalog[state.activeProductFamily][state.activeProductKey]: null},
									activeOfferKey: state => state.activeOfferKey,
									activeProductTab: state => state.activeProductTab,
									activeOfferQuantity: state => state.activeOfferQuantity,
									activeOffer: (state, getters) => {
										return state.activeOfferKey ? getters.activeProductInfo.offerings[state.activeOfferKey] 
											: getters.activeProductInfo.offerings[getters.activeProductInfo.defaultOffering];
									},
									isCableSelectionActive: (state) => {
										if (state.activeProductKey in cableSelectionRequiredOfferings && cableSelectionRequiredOfferings[state.activeProductKey].includes(state.activeOfferKey)) {
											return true;
										}
										return false;
									},
									shoppingCartIsOpen: state => state.shoppingCartIsOpen,
									selectedCategory: state => state.selectedCategory,
									expiringDiscounts: state => state.expiringDiscounts,
									total: state => state.total,
									currentDate: state => state.currentDate,
									shouldShowSinglePlan: state => {
										return !state.isPhoenixPhaseTwoCustomer && !state.isPhoenixPhaseThreeCustomer && ((state.shoppingCart.isSoftwareCartEmpty) || 
												(state.checkout.checkoutType === 'MidmarketCheckout' && !(state.checkout.isSelfServiced)) ||
												(state.checkout.smallFleetOpportunity) || 
												(state.checkout.checkoutType === 'ExpressCheckout' && state.checkout.initialPaymentTerm  === 'Net 30' && state.checkout.currencyIsoCode === 'USD')
												|| state.isPartnerAccount || state.checkout.isRenewal)
									},
									isAccountCreatedAfterTitan: state => state.isAccountCreatedAfterTitan,
									talkToSalesTimeStamp: state => state.talkToSalesTimeStamp,
									talkToSalesButtonClicked: state => state.talkToSalesButtonClicked,
									displayVehicleCableSelectionOptions: (state) => {
										const productKeysWithMultipleCableSelectionOptions = Object.entries(cableSelectionAvailableOptions)
											.filter(product => product[1].length > 1)
											.map(product => product[0]).includes(state.activeProductKey);
										return productKeysWithMultipleCableSelectionOptions;
									},
									bagStep: state => state.bagStep,
									pricingModalIsOpen: state => state.pricingModalIsOpen, 
									anonymousLargeFleet: state => state.anonymousLargeFleet,
									pricingModalComplete: state => state.pricingModalComplete,
									isAnonymousCustomer: (state) => state.isAnonymousCustomer,
									isReferralIdCustomer: (state) => state.isReferralIdCustomer,
									sfdcSegments: state => state.sfdcSegments,
									isPhoenixPhaseTwoCustomer: (state) => state.isPhoenixPhaseTwoCustomer && state.checkout.isRenewal,
									isPartnerAccount: state => state.isPartnerAccount,
									renewalDiscountMatrix: state => state.renewalDiscountMatrix,
									isPhoenixCustomer: state => state.isPhoenixCustomer,
									typeOfRenewal: state => state.typeOfRenewal,
									applicationVersion: state => state.version,
									isVGBasicCustomer: state => state.isVGBasicCustomer, 
									productSource: state => state.productSource,
									isPhoenixPhaseThreeCustomer: state => state.isPhoenixPhaseThreeCustomer,
									clickpathCotermingEnabled: state => state.clickpathCotermingEnabled
								},
								mutations: {
									setProductCatalog: (state, productCatalog) => state.productCatalog = productCatalog,
									setCurrencySymbol: (state, currencySymbol) => state.currencySymbol = currencySymbol,
									setActiveProductFamily: (state, activeProductFamily) => state.activeProductFamily = activeProductFamily,
									setActiveProductKey: (state, activeProductKey) => state.activeProductKey = activeProductKey,
									setActiveProductFamilyInactive: (state) => state.activeProductFamily = "",
									setActiveProductKeyInactive: (state) => state.activeProductKey = "",
									setActiveOfferKey: (state, activeOfferKey) => state.activeOfferKey = activeOfferKey,
									setActiveProductTab: (state, tab) => {
										state.activeProductTab = tab
										if (window) {
											window.scrollTo(0,0);
										}
									},
									setActiveProductTabDefault: (state) => 
									{
										state.activeProductTab = "overview"
										if (window) {
											window.scrollTo(0,0);
										}
									},
									setActiveOfferKeyInactive: (state) => state.activeOfferKey = null,
									setShoppingCartToOpened: (state) => state.shoppingCartIsOpen = true,
									setShoppingCartToClosed: (state) => state.shoppingCartIsOpen = false,
									setSelectedCategory: (state, selectedCategory) => state.selectedCategory = selectedCategory,
									setExpiringDiscounts: (state, expiringDiscounts) => state.expiringDiscounts = expiringDiscounts,
									setTotal: (state, total) => state.total = total,
									setCurrentDate: (state, currentDate) => state.currentDate = currentDate,
									setBagStep: (state, step) => state.bagStep = step,
									setisAccountCreatedAfterTitan: (state, isAccountCreatedAfterTitan) => state.isAccountCreatedAfterTitan = isAccountCreatedAfterTitan,
									setTalkToSalesTimeStamp: (state, talkToSalesTimeStamp) => state.talkToSalesTimeStamp = talkToSalesTimeStamp,
									setTalkToSalesButtonClicked: (state, talkToSalesButtonClicked) => state.talkToSalesButtonClicked = talkToSalesButtonClicked,
									setPricingModal: (state, value) => state.pricingModalIsOpen = value,
									setAnonymousLargeFleet: (state, value) => state.anonymousLargeFleet = value,
									setPricingModalComplete: (state) => state.pricingModalComplete = true,
									setIsAnonymousCustomer: (state, isAnonymousCustomer) => state.isAnonymousCustomer = isAnonymousCustomer,
									setIsReferralIdCustomer: (state, value) => state.isReferralIdCustomer = value,
									setSFDCSegments: (state, sfdcSegments) => state.sfdcSegments = sfdcSegments,
									setPhoenixPhaseTwoCustomer: (state, value) => state.isPhoenixPhaseTwoCustomer = value,
									setPartnerAccount: (state, value) => state.isPartnerAccount = value,
									setRenewalDiscountMatrix: (state, value) => state.renewalDiscountMatrix = value,
									setPhoenixCustomer: (state, value) => state.isPhoenixCustomer = value,
									setTypeOfRenewal: (state, value) => state.typeOfRenewal = value,
									setApplicationVersion: (state, value) => state.version = value,
									setVGBasicCustomer: (state, value) => state.isVGBasicCustomer = value,
									setProductSource: (state, value) => state.productSource = value,
									setPhoenixPhaseThreeCustomer: (state, value) => state.isPhoenixPhaseThreeCustomer = value,
									setClickpathCotermingEnabled: (state, value) => state.clickpathCotermingEnabled = value
								},
								modules: {
									account,
									shipping,
									billing,
									checkout,
									shoppingCart,
									content,
									creditCard,
									ach,
									sepa,
									bacs,
									acss,
									webstoreForm,
									...buildCableSelectionModules(cableSelection),
								},
								plugins: [createdPersistedState({ storage: window.sessionStorage })]
});

export default store;
